import { getRange } from '../../helpers/axisDef';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
  TpZoomPanelCoordMapper,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { getCanonicalTypeInfo, getColumnInfo } from '../../../../data/analyticsDataSlice';
import { LEGEND_ITEM_TYPES, renderLegendItems, renderYLabel, TpLegendItem } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import {
  createColorMappingInfo,
  getMappedColorDataPoint,
  getMappedColorLegendInfo,
} from '../../helpers/colorMappingInfo';
import { chartDims, isSelectedItem } from '../../helpers/misc';
import { perfTestStart, perfTestStop } from '../../../../data/analyticsDataStorage';
import { WebGL2DRenderTypes } from '../../../../components/webgl-2d/interfaces';
import {
  TpWebGL2DRenderData,
  TpWebGL2DRenderDataColoredPoints,
  TpWebGL2DRenderDataSimpleLines,
} from '../../../../components/webgl-2d/interface-renderData';
import { TpHoverInfo, TpHoverRenderContext } from '../../zoompan-canvas/ZoomPanCanvas';
import { color } from '../../helpers/colors';
import { getStandardDataColor } from '../../../themes/themes';
import { clipToRenderedRange, TpChartColorSchema } from '../../zoompan-canvas/helpers';
import { getUserError } from '../../../../components/miscUtils';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';

interface TpEdgesInfo {
  edgeCount: number; // number of edges
  idxFrom: number[]; // for each edge, provide the source node index
  idxTo: number[]; // for each edge, provide the destination node index
  nodesMap: { [id: string]: number }; // maps node ID to node index
  nodeLinks: { [id: string]: number[] }; // maps node ID to a list of node indexes for all linked nodes
}

// For a given nodes datatable, return the datatable containing corresponding edges of the network
function getEdgesTable(dataContext: TpDataContext, nodesDataTable: TpDataTable): TpDataTable {
  let edgesTable: TpDataTable | null = null;
  for (const otherTable of dataContext.dataTables)
    if (otherTable.relationsForEntity == nodesDataTable.canonicalTypeId) edgesTable = otherTable;
  if (!edgesTable) throw getUserError(`No relations table found for entity ${nodesDataTable.canonicalTypeId}`);
  return edgesTable;
}

// Return all relevant info about the edges for a network
function getNetWorkEdgesInfo(
  dataContext: TpDataContext,
  nodesDataTable: TpDataTable,
  nodesItemIds: any[],
): TpEdgesInfo {
  const edgesTable = getEdgesTable(dataContext, nodesDataTable);

  const nodesCanonicalType = getCanonicalTypeInfo(dataContext, nodesDataTable.canonicalTypeId);
  const nodesSelectionMap = nodesCanonicalType.selectedItemsMap;

  const edgesCanonicalType = getCanonicalTypeInfo(dataContext, edgesTable.canonicalTypeId);
  const edgesSelectionMap = edgesCanonicalType.selectedItemsMap;

  const colEdgeId = edgesTable.columnData[edgesTable.colItemId];
  const colFrom = edgesTable.columnData[edgesTable.relationFrom!];
  const colTo = edgesTable.columnData[edgesTable.relationTo!];

  const pf = perfTestStart();

  // Preparing some key node looups
  const nodesMap: { [id: string]: number } = {}; // fast lookup mapping node ids to indexes
  const selectedNodesMap: { [id: string]: boolean } = {}; // fast lookup for selection state of nodes
  const nodeLinks: { [id: string]: number[] } = {}; // fast lookup mapping node ids to list of related node indexes
  for (let i = 0; i < nodesItemIds.length; i++) {
    nodesMap[nodesItemIds[i]] = i;
    nodeLinks[nodesItemIds[i]] = [];
    if (isSelectedItem(nodesSelectionMap, nodesItemIds[i])) selectedNodesMap[nodesItemIds[i]] = true;
  }

  // Iterating over all edges & populating the info
  const idxFrom: number[] = []; // for each edge, provide the source node index
  const idxTo: number[] = []; // for each edge, provide the destination node index
  for (let i = 0; i < colFrom.length; i++) {
    if (isSelectedItem(edgesSelectionMap, colEdgeId[i])) {
      if (colFrom[i] in selectedNodesMap && colTo[i] in selectedNodesMap) {
        const nodeIdxFrom = nodesMap[colFrom[i]];
        const nodeIdxTo = nodesMap[colTo[i]];
        idxFrom.push(nodeIdxFrom);
        idxTo.push(nodeIdxTo);
        nodeLinks[colFrom[i]]?.push(nodeIdxTo);
        nodeLinks[colTo[i]]?.push(nodeIdxFrom);
      }
    }
  }

  perfTestStop(pf, `Mapping edges ==> found ${idxFrom.length}`);

  return {
    edgeCount: idxFrom.length,
    idxFrom,
    idxTo,
    nodeLinks,
    nodesMap,
  };
}

// Return the list of node IDs that are directly connected to the current selection
function getExpandedSelection(dataContext: TpDataContext, nodesDataTable: TpDataTable): string[] {
  const nodesCanonicalType = getCanonicalTypeInfo(dataContext, nodesDataTable.canonicalTypeId);
  const nodesSelectionMap = nodesCanonicalType.selectedItemsMap;
  if (!nodesSelectionMap) return []; // no seletion is active

  const edgesTable = getEdgesTable(dataContext, nodesDataTable);
  const colFrom = edgesTable.columnData[edgesTable.relationFrom!];
  const colTo = edgesTable.columnData[edgesTable.relationTo!];
  const expandedSelection: string[] = [];
  for (const selectedItemId in nodesSelectionMap) expandedSelection.push(selectedItemId);
  for (let i = 0; i < colFrom.length; i++) {
    if (colFrom[i] in nodesSelectionMap!) expandedSelection.push(colTo[i]);
    if (colTo[i] in nodesSelectionMap!) expandedSelection.push(colFrom[i]);
  }
  return expandedSelection;
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  colorSchema: TpChartColorSchema,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;
  const dataX = boundData.bindings.x.data;
  const dataY = boundData.bindings.y.data;
  const dataHoverLabel = boundData.bindings.hoverLabel?.data;
  const dataPointSize = boundData.bindings.size?.data;
  const nameX = boundData.bindings.x.columnInfo.name;
  const nameY = boundData.bindings.y.columnInfo.name;
  const title = config.customTitle ? config.customTitle : 'Network';
  const dataItemId = boundData.bindings.itemId.data;
  const pointCount = dataX.length;
  const getItemIndexById = boundData.getItemIndexById;

  const [minValX, maxValX] = getRange(dataX, 0.05);
  const [minValY, maxValY] = getRange(dataY, 0.05);

  let [minValPointSize, maxValPointSize] = [0, 1];
  if (dataPointSize) [minValPointSize, maxValPointSize] = getRange(dataPointSize, 0.05);

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;

  const opacity = config.opacity;
  const pointSizeFactor = 1.0 + 1.5 * config.pointSizeFactor ** 1.5;

  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  const colorMappingInfo = createColorMappingInfo(config, boundData, 'color', opacity, globalColorMappingConfig);

  const edgesInfo = getNetWorkEdgesInfo(dataContext, dataTable, dataItemId);

  function renderPoints(renderContext: TpAreaRenderContext) {}

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    if (colorMappingInfo.hasColors) legendItems.push(getMappedColorLegendInfo(colorMappingInfo));
    if (dataPointSize)
      legendItems.push({
        type: LEGEND_ITEM_TYPES.PointSize,
        sizeColumnInfo: getColumnInfo(dataTable, boundData.bindings.size.columnId),
        minValPointSize,
        maxValPointSize,
      });
    renderLegendItems(renderContext, legendItems);
  }

  function customHoverRenderer( // when hovering over a node, draws the relations & linked nodes as a highlight
    hoverInfo: TpHoverInfo,
    hoverContext: TpHoverRenderContext,
    ctx: CanvasRenderingContext2D,
    mapX: TpZoomPanelCoordMapper,
    mapY: TpZoomPanelCoordMapper,
    pixelRatio: number,
  ) {
    clipToRenderedRange(ctx, mapX, mapY);

    const linkedNodeNrs = edgesInfo.nodeLinks[hoverInfo.itemId];

    // Draw the linked edges
    const centerNodeNr = edgesInfo.nodesMap[hoverInfo.itemId];
    ctx.beginPath();
    ctx.strokeStyle = colorSchema.foreColor.opacity(0.75).toCanvasString();
    ctx.lineWidth = pixelRatio * 2;
    for (const nodeNr of linkedNodeNrs) {
      ctx.moveTo(mapX.map(dataX[centerNodeNr]), mapY.map(dataY[centerNodeNr]));
      ctx.lineTo(mapX.map(dataX[nodeNr]), mapY.map(dataY[nodeNr]));
    }
    ctx.stroke();

    // Draw the linked nodes
    const foreColStr = colorSchema.foreColor.opacity(0.75).toCanvasString();
    for (const nodeNr of linkedNodeNrs) {
      ctx.fillStyle = foreColStr;
      ctx.fillRect(
        mapX.map(dataX[nodeNr]) - pointSizeFactor - 2 * pixelRatio,
        mapY.map(dataY[nodeNr]) - pointSizeFactor - 2 * pixelRatio,
        2 * pointSizeFactor + 4 * pixelRatio,
        2 * pointSizeFactor + 4 * pixelRatio,
      );
      if (colorMappingInfo.hasColors)
        ctx.fillStyle = getMappedColorDataPoint(colorMappingInfo, nodeNr).toCanvasString();
      else ctx.fillStyle = foreColStr;
      ctx.fillRect(
        mapX.map(dataX[nodeNr]) - pointSizeFactor,
        mapY.map(dataY[nodeNr]) - pointSizeFactor,
        2 * pointSizeFactor,
        2 * pointSizeFactor,
      );
    }

    ctx.restore();
  }

  const hasLegend = (colorMappingInfo.hasColors && !config.hideColorLegend) || dataPointSize;
  const legendBottomSize = hasLegend ? 38 : 0;

  const padding = chartDims.padding;

  const zpLogicalRangeX = createLogicalRange(minValX, maxValX);
  const zpLogicalRangeY = createLogicalRange(minValY, maxValY);
  const zpRenderRangeX = createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding));
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + legendBottomSize),
  );

  // Create WebGL Edge lines
  const pf = perfTestStart();
  const { edgeCount, idxFrom, idxTo } = edgesInfo;
  let positsLines = new Float32Array(0);
  if (config.edgeOpacity > 0) {
    positsLines = new Float32Array(2 * 2 * edgeCount);
    for (let edgeNr = 0; edgeNr < edgeCount; edgeNr++) {
      positsLines[4 * edgeNr + 0] = dataX[idxFrom[edgeNr]];
      positsLines[4 * edgeNr + 1] = dataY[idxFrom[edgeNr]];
      positsLines[4 * edgeNr + 2] = dataX[idxTo[edgeNr]];
      positsLines[4 * edgeNr + 3] = dataY[idxTo[edgeNr]];
    }
  }
  const edgeDarknesCorr = Math.min((config.edgeDarkness * 500000) / (500000 + edgesInfo.edgeCount));
  const egdeColorVal = (1 - edgeDarknesCorr) * 255;
  const adjustedEdgeOpacity = (255 * config.edgeOpacity * 150000) / (150000 + edgesInfo.edgeCount);
  const renderLinesWebGL: TpWebGL2DRenderDataSimpleLines = {
    layerId: 'lines',
    renderType: WebGL2DRenderTypes.SIMPLELINES,
    posits: positsLines,
    color: color(egdeColorVal, egdeColorVal, egdeColorVal, adjustedEdgeOpacity),
  };
  perfTestStop(pf, 'Creating lines layer');

  let pointCountSel = 0;
  for (let pointNr = 0; pointNr < pointCount; pointNr++) {
    if (isSelectedItem(selectedItemsMap, dataItemId[pointNr])) pointCountSel++;
  }

  // Create WebGL Node points (unselected ones)
  const positsPointsUnsel = new Float32Array(2 * (pointCount - pointCountSel));
  const colorsPointsUnsel = new Float32Array(4 * (pointCount - pointCountSel));
  let pointBufferNr = 0;
  for (let pointNr = 0; pointNr < pointCount; pointNr++) {
    if (!isSelectedItem(selectedItemsMap, dataItemId[pointNr])) {
      positsPointsUnsel[2 * pointBufferNr + 0] = dataX[pointNr];
      positsPointsUnsel[2 * pointBufferNr + 1] = dataY[pointNr];
      colorsPointsUnsel[4 * pointBufferNr + 0] = 0.3;
      colorsPointsUnsel[4 * pointBufferNr + 1] = 0.3;
      colorsPointsUnsel[4 * pointBufferNr + 2] = 0.3;
      colorsPointsUnsel[4 * pointBufferNr + 3] = 0.4;
      pointBufferNr++;
    }
  }

  const renderPointsWebGLUnsel: TpWebGL2DRenderDataColoredPoints = {
    layerId: 'pointsBk',
    renderType: WebGL2DRenderTypes.COLORED_POINTS,
    posits: positsPointsUnsel,
    colors: colorsPointsUnsel,
    pointSizeFactor: pointSizeFactor,
  };

  // Create WebGL Node points (selected ones)
  const positsPointsSel = new Float32Array(2 * pointCountSel);
  const colorsPointsSel = new Float32Array(4 * pointCountSel);
  pointBufferNr = 0;
  const dataColor = getStandardDataColor();
  for (let pointNr = 0; pointNr < pointCount; pointNr++) {
    if (isSelectedItem(selectedItemsMap, dataItemId[pointNr])) {
      positsPointsSel[2 * pointBufferNr + 0] = dataX[pointNr];
      positsPointsSel[2 * pointBufferNr + 1] = dataY[pointNr];
      if (colorMappingInfo.hasColors) {
        const color = getMappedColorDataPoint(colorMappingInfo, pointNr);
        colorsPointsSel[4 * pointBufferNr + 0] = color.r / 255;
        colorsPointsSel[4 * pointBufferNr + 1] = color.g / 255;
        colorsPointsSel[4 * pointBufferNr + 2] = color.b / 255;
        colorsPointsSel[4 * pointBufferNr + 3] = opacity;
        if (isNaN(colorMappingInfo.dataColor[pointNr]))
          // if the color xoding value is not present, we want the point to be more transparent so that it does not get in the way of actual data
          colorsPointsSel[4 * pointBufferNr + 3] = 0.25 * opacity;
      } else {
        colorsPointsSel[4 * pointBufferNr + 0] = dataColor.r / 255;
        colorsPointsSel[4 * pointBufferNr + 1] = dataColor.g / 255;
        colorsPointsSel[4 * pointBufferNr + 2] = dataColor.b / 255;
        colorsPointsSel[4 * pointBufferNr + 3] = opacity;
      }
      pointBufferNr++;
    }
  }

  const renderPointsWebGLSel: TpWebGL2DRenderDataColoredPoints = {
    layerId: 'points',
    renderType: WebGL2DRenderTypes.COLORED_POINTS,
    posits: positsPointsSel,
    colors: colorsPointsSel,
    pointSizeFactor,
  };

  const webGL2DRenderData: TpWebGL2DRenderData = {
    backColor: colorSchema.backColor,
    layers: [renderPointsWebGLUnsel, renderLinesWebGL, renderPointsWebGLSel],
  };

  const areaPoints: TpChartAreaDefinition = {
    id: 'points',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    canAreaSelect: 'xy',
    render: renderPoints,
    webGL2DRenderData,
  };

  const chartAreas: TpChartAreaDefinition[] = [areaPoints];

  if (!config.hideAxes)
    chartAreas.push({
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, '', title),
    });

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaPoints,
    dataTable,
    dataX,
    dataY,
    dataItemId,
    dataHoverLabel,
    getItemIndexById,
    pointCount,
    nameX,
    nameY,
    colInfoX: getColumnInfo(dataTable, boundData.bindings.x.columnId),
    colInfoY: getColumnInfo(dataTable, boundData.bindings.y.columnId),
    colInfoHover: boundData.bindings.hoverLabel?.columnId
      ? getColumnInfo(dataTable, boundData.bindings.hoverLabel?.columnId)
      : null,
    edgesInfo,
    customHoverRenderer,
    getExpandedSelection: selectedItemsMap ? () => getExpandedSelection(dataContext, dataTable) : null,
    title,
  };
}
